/* ============================================================================
 * Surface math for the sign-in fabric.
 *
 * Each sheet is a ribbon of cloth: a curved centreline (u: 0 → 1 along its
 * length) with a cross-section (v: -1 → 1) that twists and folds over time.
 * `sheetPoint` is the single source of truth — FABRIC_GLSL below is a line-by-
 * line port of it, so DOM annotations can be pinned to the exact point of the
 * cloth the GPU is drawing.
 * ========================================================================== */
export const CAMERA_Z = 5;
const FOV = (30 * Math.PI) / 180;
const lerp = (a, b, t) => a + (b - a) * t;
export function sheetPoint(s, u, v, time) {
    const t = time * s.speed + s.phase;
    const x = lerp(s.x0, s.x1, u);
    const y = lerp(s.y0, s.y1, u) + s.bow * Math.sin(Math.PI * u) + 0.08 * Math.sin(u * 5 + t * 0.6);
    const z = s.zBase + s.zAmp * Math.sin(u * 2.4 + t * 0.35);
    const ang = s.twist0 + s.twist1 * u + 0.28 * Math.sin(t * 0.5 + u * 2.2);
    const hw = s.width * (0.82 + 0.18 * Math.sin(u * 3.3 + t * 0.4));
    // Cross-section stays perpendicular to the centreline, then turns about it.
    const dx = s.x1 - s.x0;
    const dy = s.y1 - s.y0 + s.bow * Math.PI * Math.cos(Math.PI * u) + 0.4 * Math.cos(u * 5 + t * 0.6);
    const dl = Math.hypot(dx, dy);
    const px = -dy / dl;
    const py = dx / dl;
    const ca = Math.cos(ang);
    const sa = Math.sin(ang);
    const fold = s.fold *
        (0.62 * Math.sin(v * 4.6 + u * 3.1 - t * 0.7) + 0.26 * Math.sin(v * 9.7 - u * 4.3 + t * 0.55)) *
        (1 - 0.35 * v * v);
    const bulge = 0.14 * (1 - v * v) * Math.sin(u * 3.7 + t * 0.45);
    const k = (fold + bulge) * hw;
    const a = v * hw;
    return [x + px * (ca * a - sa * k), y + py * (ca * a - sa * k), z + sa * a + ca * k];
}
export const FABRIC_GLSL = /* glsl */ `
uniform vec4 uP0; // x0 x1 y0 y1
uniform vec4 uP1; // bow zAmp width twist0
uniform vec4 uP2; // twist1 fold phase speed
uniform vec4 uP3; // zBase lines opacity edge
uniform float uTime;

// Fold displacement as a fraction of the half-width (negative = valley).
float foldAt(vec2 uv) {
  float u = uv.x;
  float v = uv.y;
  float t = uTime * uP2.w + uP2.z;
  float fold = uP2.y * (0.62 * sin(v * 4.6 + u * 3.1 - t * 0.7) + 0.26 * sin(v * 9.7 - u * 4.3 + t * 0.55)) * (1.0 - 0.35 * v * v);
  return fold + 0.14 * (1.0 - v * v) * sin(u * 3.7 + t * 0.45);
}

vec3 sheet(vec2 uv) {
  float u = uv.x;
  float v = uv.y;
  float t = uTime * uP2.w + uP2.z;
  float x = mix(uP0.x, uP0.y, u);
  float y = mix(uP0.z, uP0.w, u) + uP1.x * sin(3.14159265 * u) + 0.08 * sin(u * 5.0 + t * 0.6);
  float z = uP3.x + uP1.y * sin(u * 2.4 + t * 0.35);
  float ang = uP1.w + uP2.x * u + 0.28 * sin(t * 0.5 + u * 2.2);
  float hw = uP1.z * (0.82 + 0.18 * sin(u * 3.3 + t * 0.4));
  float dx = uP0.y - uP0.x;
  float dy = uP0.w - uP0.z + uP1.x * 3.14159265 * cos(3.14159265 * u) + 0.4 * cos(u * 5.0 + t * 0.6);
  vec2 perp = vec2(-dy, dx) / length(vec2(dx, dy));
  float ca = cos(ang);
  float sa = sin(ang);
  float k = foldAt(uv) * hw;
  float a = v * hw;
  return vec3(x + perp.x * (ca * a - sa * k), y + perp.y * (ca * a - sa * k), z + sa * a + ca * k);
}
`;
/* -------------------------------- Layout -------------------------------- */
/**
 * Builds the composition for a canvas of the given size. `focus` is the
 * horizontal centre of the visual area as a fraction of the canvas width and
 * `span` its width fraction — on desktop the form column covers the rest.
 */
export function buildScene(width, height, variant, focus = 0.5, span = 1) {
    const aspect = width / Math.max(height, 1);
    const halfH = Math.tan(FOV / 2) * CAMERA_Z;
    const halfW = halfH * aspect;
    const cx = (focus * 2 - 1) * halfW;
    // Composition units: x spans the visual area, y and widths the full height.
    const VW = halfW * span;
    const X = (n) => cx + n * VW;
    const Y = (n) => n * halfH;
    const base = { phase: 0, speed: 1, lines: 0, opacity: 1, edge: 1, zBase: 0 };
    if (variant === 'full') {
        return {
            halfW,
            halfH,
            cx,
            segments: [180, 64],
            sheets: [
                Object.assign(Object.assign({}, base), { x0: X(-0.9), y0: Y(1.65), x1: X(1.45), y1: Y(-0.75), bow: Y(0.2), zAmp: 0.3, zBase: -1.1, width: Y(0.62), twist0: 0.7, twist1: -1.5, fold: 0.24, phase: 2.1, speed: 0.85, opacity: 0.96, edge: 0 }),
                Object.assign(Object.assign({}, base), { x0: X(-1.65), y0: Y(0.95), x1: X(0.62), y1: Y(-1.7), bow: Y(0.5), zAmp: 0.35, width: Y(0.86), twist0: -0.45, twist1: 1.75, fold: 0.32, edge: 0 }),
                Object.assign(Object.assign({}, base), { x0: X(-1.3), y0: Y(-1.15), x1: X(3.1), y1: Y(-0.5), bow: Y(0.12), zAmp: 0.2, zBase: -0.2, width: Y(0.04), twist0: 0.2, twist1: 7, fold: 0, phase: 4, speed: 0.7, edge: 0.9 }),
            ],
        };
    }
    const wide = width >= 640;
    const sheets = [
        Object.assign(Object.assign({}, base), { x0: X(-1.6), y0: Y(1.1), x1: X(1.4), y1: Y(-1.25), bow: Y(0.45), zAmp: 0.3, width: Y(wide ? 0.78 : 0.86), twist0: -0.45, twist1: 1.7, fold: 0.3, edge: 0 }),
        Object.assign(Object.assign({}, base), { x0: X(-1.3), y0: Y(-0.5), x1: X(1.3), y1: Y(0.3), bow: Y(-0.12), zAmp: 0.15, zBase: -0.3, width: Y(0.05), twist0: 0.2, twist1: 6.5, fold: 0, phase: 4, speed: 0.7, edge: 0.9 }),
    ];
    if (wide) {
        // Back layer, tablet only.
        sheets.unshift(Object.assign(Object.assign({}, base), { x0: X(-1.0), y0: Y(1.55), x1: X(1.45), y1: Y(-0.45), bow: Y(0.2), zAmp: 0.25, zBase: -1.1, width: Y(0.66), twist0: 0.7, twist1: -1.4, fold: 0.22, phase: 2.1, speed: 0.85, opacity: 0.96, edge: 0 }));
    }
    return { halfW, halfH, cx, segments: wide ? [140, 48] : [100, 36], sheets };
}
/* ------------------------------ Matrices -------------------------------- */
// Column-major, as WebGL expects.
export function mat4Multiply(a, b) {
    const out = new Float32Array(16);
    for (let c = 0; c < 4; c += 1) {
        for (let r = 0; r < 4; r += 1) {
            out[c * 4 + r] =
                a[r] * b[c * 4] +
                    a[4 + r] * b[c * 4 + 1] +
                    a[8 + r] * b[c * 4 + 2] +
                    a[12 + r] * b[c * 4 + 3];
        }
    }
    return out;
}
export function perspective(aspect) {
    const f = 1 / Math.tan(FOV / 2);
    const near = 0.1;
    const far = 30;
    // prettier-ignore
    return new Float32Array([
        f / aspect, 0, 0, 0,
        0, f, 0, 0,
        0, 0, (far + near) / (near - far), -1,
        0, 0, (2 * far * near) / (near - far), 0,
    ]);
}
export function modelMatrix(tx, ty, rx, ry, rz) {
    const [cx, sx, cy, sy, cz, sz] = [
        Math.cos(rx),
        Math.sin(rx),
        Math.cos(ry),
        Math.sin(ry),
        Math.cos(rz),
        Math.sin(rz),
    ];
    // prettier-ignore
    const rotX = new Float32Array([1, 0, 0, 0, 0, cx, sx, 0, 0, -sx, cx, 0, 0, 0, 0, 1]);
    // prettier-ignore
    const rotY = new Float32Array([cy, 0, -sy, 0, 0, 1, 0, 0, sy, 0, cy, 0, 0, 0, 0, 1]);
    // prettier-ignore
    const rotZ = new Float32Array([cz, sz, 0, 0, -sz, cz, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
    // prettier-ignore
    const move = new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, tx, ty, -CAMERA_Z, 1]);
    return mat4Multiply(move, mat4Multiply(rotY, mat4Multiply(rotX, rotZ)));
}
export function transformPoint(m, p) {
    const [x, y, z] = p;
    return [
        m[0] * x + m[4] * y + m[8] * z + m[12],
        m[1] * x + m[5] * y + m[9] * z + m[13],
        m[2] * x + m[6] * y + m[10] * z + m[14],
        m[3] * x + m[7] * y + m[11] * z + m[15],
    ];
}
/** World point → CSS pixels within a canvas of `width` × `height`. */
export function toScreen(viewProj, p, width, height) {
    const [x, y, , w] = transformPoint(viewProj, p);
    return [((x / w) * 0.5 + 0.5) * width, (1 - ((y / w) * 0.5 + 0.5)) * height];
}
/** Interleaved (u, v) grid and triangle indices for one sheet. */
export function buildGrid(su, sv) {
    const uv = new Float32Array((su + 1) * (sv + 1) * 2);
    let k = 0;
    for (let j = 0; j <= sv; j += 1) {
        for (let i = 0; i <= su; i += 1) {
            uv[k++] = i / su;
            uv[k++] = (j / sv) * 2 - 1;
        }
    }
    const idx = new Uint16Array(su * sv * 6);
    k = 0;
    for (let j = 0; j < sv; j += 1) {
        for (let i = 0; i < su; i += 1) {
            const a = j * (su + 1) + i;
            const b = a + su + 1;
            idx[k++] = a;
            idx[k++] = b;
            idx[k++] = a + 1;
            idx[k++] = a + 1;
            idx[k++] = b;
            idx[k++] = b + 1;
        }
    }
    return { uv, idx };
}
